import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import { styled, useTheme, ThemeProvider } from "@mui/material/styles";
import { TreeView, TreeItem } from "@mui/x-tree-view";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar"; // Added Snackbar import
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import ReactDOM from "react-dom";
import MobileStepper from "@mui/material/MobileStepper";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
// import { TimePicker } from "@mui/x-date-pickers/TimePicker";

import {
  collection,
  query as firebaseQuery, // Aliased to avoid conflict with local 'query' variable in performSearch
  where,
  onSnapshot,
  updateDoc,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
  increment,
  writeBatch,
} from "firebase/firestore";
import { db, auth, storage } from "../../firebase/firebase.js";
import { ensureCoordinatorDrafts } from "../../services/agentService.js";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import {
  getTodayString,
  fetchTodayData,
  clearTodayData,
} from "../../utils/todayData.js";
import {
  fetchLastResetTime,
  updateLastResetTime,
  setLastUpdated,
  durationToDate,
  dateToDuration,
} from "../../utils/timerUtils.js";
import { buildTree, getNodeAndDescendants } from "../../utils/buildTree.js";
import { runTransaction } from "firebase/firestore";
import { fetchUserData, updateUserData } from "../../utils/userData.js";
import { updateActivity } from "./api/activity.js";
import {
  // sendMessageToChatGPT,
  generateImage,
  embedText,
} from "./api/geminiClient.js";
import { DeleteDialog } from "./dialogs/DeleteDialog.js";
import { DecrementDialog } from "./dialogs/DecrementDialog.js";
import { ChatBox } from "./ChatBox.js";
import TreeNodeLabel from "./TreeNodeLabel.jsx";
import { ImageButtonsContext } from "../../context/ImageButtonsContext";
import { TimerContext } from "../../context/TimerContext";
import { ChatVisibilityContext } from "../../context/ChatVisibilityContext";
import { PointsVisibilityContext } from "../../context/PointsVisibilityContext"; // Added
import { NodeButtonsContext } from "../../context/NodeButtonsContext";
import { ImageVisibilityContext } from "../../context/ImageVisibilityContext";
import { useDashboardSearch } from "./hooks/useDashboardSearch";

// Add a constant for the height of your navbar
const navbarHeight = "70px";
const chatboxHeight = "70px";
let talking = false;

// The Component
const Dashboard = () => {
  const inputRef = useRef(null);
  const theme = useTheme();
  const dividerPositionRef = useRef();
  const treeViewRef = useRef(null);
  const highlightTimeoutRef = useRef();
  const scrollTimeoutRef = useRef();
  const { showImageButtons } = useContext(ImageButtonsContext);
  const { showTimer } = useContext(TimerContext);
  const { showPoints } = useContext(PointsVisibilityContext); // Added
  const { showNodeButtons } = useContext(NodeButtonsContext);
  const { showImages } = useContext(ImageVisibilityContext);

  // const [leftPanelWidth, setLeftPanelWidth] = useState(50); // Percentage of the left panel width
  const [UID, setUID] = useState(null);
  const [progressTree, setProgressTree] = useState(null);
  const [todayActivities, setTodayActivities] = useState({});
  const [progressData, setProgressData] = useState(null);
  const [maxPoints, setMaxPoints] = useState(0);
  const [editingNode, setEditingNode] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDailyPoints, setEditingDailyPoints] = useState(false);
  const [editingUnit, setEditingUnit] = useState(false);
  const [newNodeId, setNewNodeId] = useState(null);
  const [timer, setTimer] = useState(600); // = 10 minutes
  const [durationToAlert, setDurationToAlert] = useState(600); // = 10 minutes
  const [showAlert, setShowAlert] = useState(false);
  const [stopd, setStop] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [nodeToDecrement, setNodeToDecrement] = useState(null);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [deleteHasChildren, setDeleteHasChildren] = useState(false);
  const [agents, setAgents] = useState([]);
  const [coreMap, setCoreMap] = useState({});
  const [horizontal, setHorizontal] = useState(true);
  const { chatExpanded, setChatExpanded } = useContext(ChatVisibilityContext);
  const [expandedItems, setExpandedItems] = useState([]);
  const [timerDialogOpen, setTimerDialogOpen] = useState(false);
  const [newTimerHours, setNewTimerHours] = useState(Math.floor(timer / 3600));
  const [newTimerMinutes, setNewTimerMinutes] = useState(
    Math.floor((timer % 3600) / 60)
  );
  const [newTimerSeconds, setNewTimerSeconds] = useState(timer % 60);
  const [imageGeneratingForNodes, setImageGeneratingForNodes] = useState([]);
  const [highlightedNodes, setHighlightedNodes] = useState({});
  const [pendingSuggestions, setPendingSuggestions] = useState(null);
  const {
    searchQuery,
    setSearchQuery,
    isSearching,
    setIsSearching,
    searchResults,
    setSearchResults,
    currentSearchResultIndex,
    setCurrentSearchResultIndex,
    clearSearch,
  } = useDashboardSearch();
  const [snack, setSnack] = useState({ message: "", severity: "info" }); // Added snack state

  const handleClearSearch = () => {
    clearSearch();
    setHighlightedNodes({});
  };

  const toggleNodeUrgentStatus = async (node) => {
    if (!node || !node.id) {
      console.error("Invalid node provided to toggleNodeUrgentStatus", node);
      return;
    }
    try {
      const nodeRef = doc(db, "progress", node.id);
      const newUrgentStatus = !(node.isUrgent || false);
      await updateDoc(nodeRef, {
        isUrgent: newUrgentStatus,
      });

      // Update local state
      setProgressData((prevProgressData) => {
        if (!prevProgressData) return null;

        const updateNodeInTree = (currentNode) => {
          if (currentNode.id === node.id) {
            return { ...currentNode, isUrgent: newUrgentStatus };
          }
          if (currentNode.children) {
            return {
              ...currentNode,
              children: currentNode.children.map(updateNodeInTree),
            };
          }
          return currentNode;
        };

        return updateNodeInTree(prevProgressData);
      });
    } catch (error) {
      console.error("Error toggling node urgent status:", error);
    }
  };

  useEffect(() => {
    return () => {
      clearTimeout(highlightTimeoutRef.current);
      clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setHorizontal(window.innerWidth > window.innerHeight ? true : false);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Call the function initially

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(highlightTimeoutRef.current);
      clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const loadCore = async () => {
      const snap = await getDocs(collection(db, "coreAgents"));
      const map = {};
      snap.docs.forEach((d) => (map[d.id] = { id: d.id, ...d.data() }));
      setCoreMap(map);
    };
    loadCore();
  }, []);

  useEffect(() => {
    if (!auth.currentUser || !auth.currentUser.uid) {
      // Optionally log that listeners are not being set up due to missing auth info
      console.log(
        "Dashboard listeners: User not fully authenticated yet. Skipping listener setup."
      );
      // Clear any existing listeners if necessary, or ensure this effect's cleanup handles it.
      // For now, just returning will prevent setup. If this effect re-runs with valid auth, it will set them up.
      return;
    }
    const userID = auth.currentUser.uid; // Now this is safe
    // The rest of the useEffect logic follows, using 'userID'
    setUID(userID);

    // make sure coordinator agents exist for this user
    ensureCoordinatorDrafts(userID);

    /* listen to users/{uid}/agents */
    const agentsUnsub = onSnapshot(
      collection(db, "users", userID, "agents"),
      (snap) => {
        setAgents(
          snap.docs.map((d) => ({
            id: d.id,
            ...(coreMap[d.id] || {}),
            ...d.data(),
          }))
        );
      }
    );

    // const checkAndUpdateTimer = async () => {
    //   const lastResetTime = await fetchLastResetTime(userID);
    //   if (
    //     !lastResetTime ||
    //     new Date().getTime() - new Date(lastResetTime).getTime() >
    //       durationToAlert * 1000
    //   ) {
    //     await updateLastResetTime(userID);
    //   }
    // };

    // checkAndUpdateTimer();

    const retrieveDurationToAlert = async () => {
      const userInfo = await fetchUserData(userID);
      setDurationToAlert(
        userInfo && userInfo.durationToAlert ? userInfo.durationToAlert : 600
      );
    };
    retrieveDurationToAlert();

    const timestampsUnsubscribe = onSnapshot(
      doc(db, "timestamps", userID),
      async (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const lastResetTime = new Date(data.lastResetTime.toDate()).getTime();
          const timeDiff = Math.floor(
            (new Date().getTime() - lastResetTime) / 1000
          );
          const userInfo = await fetchUserData(userID);
          const durationToAlertTemp =
            userInfo && userInfo.durationToAlert
              ? userInfo.durationToAlert
              : 600;
          if (timeDiff <= durationToAlertTemp) {
            setTimer(durationToAlertTemp - timeDiff);
          } else {
            setTimer(durationToAlertTemp);
            await updateLastResetTime(userID);
          }
        }
      }
    );

    const q = firebaseQuery(
      collection(db, "progress"),
      where("uid", "==", userID)
    );
    const progressUnsubscribe = onSnapshot(q, (querySnapshot) => {
      const data = [];
      let maxPointsTemp = 0;

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        const nodeData = { id: doc.id, ...docData };
        data.push(nodeData);
        if (nodeData.title !== "Life" && nodeData.points > maxPointsTemp) {
          maxPointsTemp = nodeData.points;
        }
      });
      const treeObject = buildTree(data);

      setProgressTree(treeObject);
      setMaxPoints(maxPointsTemp === 0 ? 1 : maxPointsTemp);
    });

    const todayUnsubscribe = onSnapshot(
      doc(db, "today", userID),
      async (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const todayString = getTodayString();
          if (!data || !data.date || data.date !== todayString) {
            await clearTodayData(userID);
            setTodayActivities({});
          } else if (data.activities) {
            setTodayActivities(data.activities);
          } else {
            setTodayActivities({});
          }
        } else {
          await clearTodayData(userID);
          setTodayActivities({});
        }
      }
    );

    return () => {
      timestampsUnsubscribe();
      progressUnsubscribe();
      todayUnsubscribe();
      agentsUnsub();
    };
  }, [db, auth, auth.currentUser, coreMap]);

  useEffect(() => {
    if (!progressTree) {
      setProgressData(progressTree);
      return;
    }
    const applyToday = (node) => {
      const children = node.children ? node.children.map(applyToday) : [];
      const childrenToday = children.reduce(
        (sum, child) => sum + (child.today || 0),
        0
      );
      return {
        ...node,
        children,
        today: (todayActivities[node.id]?.points || 0) + childrenToday,
      };
    };
    setProgressData(applyToday(progressTree));
  }, [progressTree, todayActivities]);

  useEffect(() => {
    let interval = null;
    if (!stopd && showTimer) {
      interval = setInterval(() => {
        if (timer > 0) {
          // if (timer % 60 === 0 && !talking) {
          //   setAgents((preAgents) => {
          //     for (let agent of preAgents) {
          //       const otherAgents = [];
          //       preAgents.forEach((otherAgent) => {
          //         if (otherAgent.name !== agent.name) {
          //           otherAgents.push(otherAgent.name);
          //         }
          //       });
          //       if (!agent.lastResetTime) {
          //         setLastUpdated(agent.task, UID);
          //       } else if (
          //         agent.lastResetTime.toDate().getTime() +
          //           agent.spacing * 60000 <
          //         new Date().getTime()
          //       ) {
          //         sendMessageToChatGPT(
          //           agent,
          //           otherAgents.join(", "),
          //           "",
          //           filterProgress(progressData, agent.task)
          //         );
          //         talking = true;
          //         setTimeout(() => {
          //           talking = false;
          //         }, 13000);
          //         break;
          //       }
          //     }
          //     return preAgents;
          //   });
          // }
          setTimer(timer - 1);
        } else {
          setShowAlert(true);
          updateLastResetTime(UID);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [db, UID, timer, stopd, showTimer]);

  const resetTimer = () => {
    if (!showTimer) return;
    updateLastResetTime(UID); // Reset to 10 minutes
    setShowAlert(false);
  };

  const stopPlay = () => {
    if (!showTimer) return;
    if (stopd) {
      agents.forEach((agent) => {
        setLastUpdated(agent.id, UID);
        updateLastResetTime(UID);
        setTimer(durationToAlert);
      });
    }
    setStop((prev) => !prev);
  };

  // const handleDragStart = (e) => {
  //   e.preventDefault();
  //   dividerPositionRef.current = (e.clientX / window.innerWidth) * 100;
  //   window.addEventListener("mousemove", handleDragging);
  //   window.addEventListener("mouseup", handleDragEnd);
  // };

  // const handleDragging = (e) => {
  //   // Only update the position if the mouse has moved a significant amount (e.g., more than 5 pixels)
  //   if (
  //     !dividerPositionRef.current ||
  //     Math.abs(dividerPositionRef.current - e.clientX) > 5
  //   ) {
  //     const newWidth = (e.clientX / window.innerWidth) * 100;
  //     setLeftPanelWidth(newWidth);
  //     dividerPositionRef.current = e.clientX;
  //   }
  // };

  // const handleDragEnd = () => {
  //   window.removeEventListener("mousemove", handleDragging);
  //   window.removeEventListener("mouseup", handleDragEnd);
  // };

  const updateNodePoints = async (event, node, incrementValue) => {
    event.stopPropagation(); // Prevent event from bubbling up

    setHighlightedNodes((prev) => {
      const copy = { ...prev };
      delete copy[node.id];
      return copy;
    });

    if (node.parent === "Life") {
      await setLastUpdated(node.agent, UID);
    }

    const updatePoints = async (currentNode) => {
      const docRef = doc(db, "progress", currentNode.id);
      await updateDoc(docRef, { points: increment(incrementValue) });

      if (currentNode.parent && currentNode.parent !== "Life") {
        const q = firebaseQuery(
          collection(db, "progress"),
          where("title", "==", currentNode.parent),
          where("uid", "==", UID)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          querySnapshot.forEach(async (docSnap) => {
            await updatePoints({ ...docSnap.data(), id: docSnap.id });
          });
        }
      }
    };

    await updatePoints(node);

    // After updating points, update the activity in the 'today' collection
    await updateActivity(node, incrementValue);

    resetTimer(); // Reset the timer on each point update
  };

  const handleDecrementOpen = (event, node) => {
    event.stopPropagation();
    setNodeToDecrement(node);
    setConfirmDialogOpen(true);
  };

  const handleDecrementConfirm = () => {
    if (nodeToDecrement) {
      updateNodePoints(new Event("click"), nodeToDecrement, -1);
    }
    setConfirmDialogOpen(false);
    setNodeToDecrement(null);
  };

  const handleDecrementCancel = () => {
    setConfirmDialogOpen(false);
    setNodeToDecrement(null);
  };

  const handleDeleteOpen = (event, node) => {
    event.stopPropagation();
    setNodeToDelete(node);
    setDeleteHasChildren(node.children && node.children.length > 0);
    setDeleteConfirmDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (nodeToDelete && !deleteHasChildren) {
      await deleteDoc(doc(db, "progress", nodeToDelete.id));
    }
    setDeleteConfirmDialogOpen(false);
    setNodeToDelete(null);
    setDeleteHasChildren(false);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmDialogOpen(false);
    setNodeToDelete(null);
    setDeleteHasChildren(false);
  };

  const handleTitleChange = (event, node) => {
    setEditingNode({ ...node, title: event.target.value });
  };

  const handleDailyPointsChange = (event, node) => {
    setEditingNode({
      ...node,
      dailyPoints:
        isNaN(Number(event.target.value)) || Number(event.target.value) <= 0
          ? 10
          : Number(event.target.value),
    });
  };

  const handleUnitChange = (event, node) => {
    setEditingNode({ ...node, unit: event.target.value });
  };

  const handleTitleSubmit = async (node) => {
    let titleChanged = false;
    if (node.title !== editingNode.title && editingNode.title !== "") {
      let q = firebaseQuery(
        collection(db, "progress"),
        where("title", "==", editingNode.title),
        where("uid", "==", UID)
      );
      let querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        alert(
          "There is another node with the same title. Please choose a different one."
        );
      } else {
        const batch = writeBatch(db);

        const docRef = doc(db, "progress", node.id);
        batch.update(docRef, { title: editingNode.title });

        // Fetch and update child nodes
        q = firebaseQuery(
          collection(db, "progress"),
          where("parent", "==", node.title),
          where("uid", "==", UID)
        );
        querySnapshot = await getDocs(q);
        querySnapshot.forEach((docSnap) => {
          batch.update(doc(db, "progress", docSnap.id), {
            parent: editingNode.title,
          });
        });

        await batch.commit();
        titleChanged = true;
      }
    }
    setEditingTitle(false);
    setEditingNode(null);
    setNewNodeId(null); // Update the new node ID
    if (titleChanged) {
      // await generateImageForTitleAndAllDescendants("Nutrition");
      // await generateImageForTitleAndAllDescendants("Exercise");
    }
  };

  // Recursively collects titles from the given node and all its descendants,
  // omitting "Life" from the fullTitle.
  const getAllTitles = (node, ancestors = []) => {
    // Filter out "Life" from the ancestors list
    const filteredAncestors = ancestors.filter((title) => title !== "Life");
    const fullTitle =
      filteredAncestors.length > 0
        ? `${filteredAncestors.join(" - ")} - ${node.title}`
        : node.title;
    let titles = [{ title: node.title, fullTitle }];
    if (node.children && node.children.length > 0) {
      node.children.forEach((child) => {
        titles.push(...getAllTitles(child, [...ancestors, node.title]));
      });
    }
    return titles;
  };

  // Searches a tree for a node with the specified title and returns its title chain (including all descendants)
  const getTitlesByNodeTitle = (targetTitle) => {
    let result = [];
    const searchTree = (node) => {
      if (node.title === targetTitle) {
        result = getAllTitles(node);
        return true; // Found, no need to search deeper
      }
      if (node.children) {
        for (let child of node.children) {
          if (searchTree(child)) {
            return true;
          }
        }
      }
      return false;
    };
    searchTree(progressData);
    return result;
  };

  const generateImageForTitleAndAllDescendants = async (targetTitle) => {
    const titles = getTitlesByNodeTitle(targetTitle);
    console.log("Titles:", titles);
    for (const { title, fullTitle } of titles) {
      const q = firebaseQuery(
        collection(db, "progress"),
        where("title", "==", title),
        where("uid", "==", UID)
      );
      const querySnapshot = await getDocs(q);
      let shouldGenerate = false;
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (!data.image) {
          shouldGenerate = true;
        }
      });
      if (shouldGenerate) {
        setImageGeneratingForNodes((prev) =>
          prev.includes(title) ? prev : [...prev, title]
        );
        await saveImageToGCS(fullTitle, title);
      }
    }
  };

  const regenerateImageForTitle = async (targetTitle) => {
    setImageGeneratingForNodes((prev) =>
      prev.includes(targetTitle) ? prev : [...prev, targetTitle]
    );
    const fullTitle = getFullTitleByNodeTitle(targetTitle);
    console.log("Target Title:", targetTitle, ", Full Title:", fullTitle);
    await saveImageToGCS(fullTitle, targetTitle);
  };

  const getFullTitleByNodeTitle = (targetTitle) => {
    // Recursive helper to search the tree and record the path
    const findPath = (node, targetTitle, path = []) => {
      if (node.title === targetTitle) {
        return [...path, node];
      }
      if (node.children) {
        for (const child of node.children) {
          const result = findPath(child, targetTitle, [...path, node]);
          if (result) return result;
        }
      }
      return null;
    };

    if (!progressData) return "";
    const path = findPath(progressData, targetTitle);
    if (!path) {
      console.warn(`Node with title "${targetTitle}" not found.`);
      return "";
    }
    // Exclude the ancestor with title "Life" (assumed to be the root)
    const filteredPath = path.filter((node, index) => !(node.title === "Life"));
    // Build the full title string from the ancestor chain.
    // For example: "Grandparent - Parent - Node"
    return filteredPath.map((node) => node.title).join(" - ");
  };

  const saveImageToGCS = async (fullTitle, title) => {
    console.log("Generating image for:", title);
    try {
      const description = `A very simple and realistic image with a white background to show "${fullTitle}". The image should be very simple and realistic, and should not include more than a few objects or characters.`;

      // Generate the base64 representation of the image
      const b64Image = await generateImage(description, "1024x1024");

      // Convert base64 string to a Blob so it can be uploaded
      const binary = atob(b64Image);
      const array = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        array[i] = binary.charCodeAt(i);
      }
      const imageBlob = new Blob([array], { type: "image/png" });

      // Create a unique file name under the "/images" folder
      const fileName = `images/${Date.now()}_${title}.png`;

      // Create a reference to this file in Cloud Storage
      const fileRef = ref(storage, fileName);

      // Upload the blob to Cloud Storage
      await uploadBytes(fileRef, imageBlob, { contentType: "image/png" });

      // Get the public URL for the uploaded image
      const publicUrl = await getDownloadURL(fileRef);

      const q = firebaseQuery(
        collection(db, "progress"),
        where("title", "==", title),
        where("uid", "==", UID)
      );
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach(async (document) => {
        await updateDoc(doc(db, "progress", document.id), {
          image: publicUrl,
        });
      });
      setImageGeneratingForNodes((prev) =>
        prev.filter((item) => item !== title)
      );
      console.log("Generated image for:", title, "at", publicUrl);
    } catch (error) {
      console.error("Error saving image to GCS:", error);
      throw error;
    }
  };

  const saveCustomImageToGCS = async (title) => {
    console.log("Saving custom image for:", title);
    try {
      setImageGeneratingForNodes((prev) =>
        prev.includes(title) ? prev : [...prev, title]
      );
      // Open a custom MUI dialog to get the image file or URL from the user.
      const getImageFromUser = () => {
        return new Promise((resolve) => {
          const dialogContainer = document.createElement("div");
          document.body.appendChild(dialogContainer);

          const ImageDialog = () => {
            const [open, setOpen] = React.useState(true);
            const [mode, setMode] = React.useState("file");
            const [file, setFile] = React.useState(null);
            const [url, setUrl] = React.useState("");

            const previewSrc =
              mode === "file" && file
                ? URL.createObjectURL(file)
                : mode === "url" && url
                ? url
                : null;

            const handleClose = (approved) => {
              setOpen(false);
              setTimeout(() => {
                ReactDOM.unmountComponentAtNode(dialogContainer);
                document.body.removeChild(dialogContainer);
              }, 100);
              if (approved) {
                resolve({ mode, file, url });
              } else {
                resolve(null);
              }
            };

            return (
              <Dialog
                open={open}
                onClose={() => handleClose(false)}
                fullWidth
                maxWidth="xl"
              >
                <DialogTitle>Select Image for "{title}"</DialogTitle>
                <DialogContent>
                  <RadioGroup
                    row
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                  >
                    <FormControlLabel
                      value="file"
                      control={<Radio />}
                      label="Upload"
                    />
                    <FormControlLabel
                      value="url"
                      control={<Radio />}
                      label="Image URL"
                    />
                  </RadioGroup>
                  {mode === "file" ? (
                    <Box mt={2}>
                      <Button variant="outlined" component="label">
                        Choose File
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={(e) => setFile(e.target.files[0])}
                        />
                      </Button>
                      {file && (
                        <Typography variant="body2" sx={{ ml: 2 }}>
                          {file.name}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <TextField
                      autoFocus
                      margin="dense"
                      label="Image URL"
                      type="url"
                      fullWidth
                      variant="outlined"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      sx={{ mt: 2 }}
                    />
                  )}
                  {previewSrc && (
                    <Box mt={2}>
                      <Box
                        component="img"
                        src={previewSrc}
                        alt="Image Preview"
                        sx={{
                          width: "100%",
                          maxHeight: "600px",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => handleClose(false)}>Cancel</Button>
                  <Button onClick={() => handleClose(true)} color="primary">
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>
            );
          };

          ReactDOM.render(
            <ThemeProvider theme={theme}>
              <ImageDialog />
            </ThemeProvider>,
            dialogContainer
          );
        });
      };

      const result = await getImageFromUser();
      if (!result) {
        setImageGeneratingForNodes((prev) =>
          prev.filter((item) => item !== title)
        );
        console.error("User cancelled or did not provide an image.");
        return;
      }

      let blob;
      let extension = ".png";
      if (result.mode === "file" && result.file) {
        blob = result.file;
        const match = result.file.name.match(/\.([a-zA-Z0-9]+)$/);
        if (match) extension = `.${match[1]}`;
      } else if (result.mode === "url" && result.url) {
        const response = await fetch(result.url);
        if (!response.ok) throw new Error("Failed to fetch image");
        blob = await response.blob();
        const match = result.url.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/);
        if (match) extension = `.${match[1]}`;
      } else {
        setImageGeneratingForNodes((prev) =>
          prev.filter((item) => item !== title)
        );
        console.error("No image provided");
        return;
      }

      const fileName = `images/${Date.now()}_${title}${extension}`;
      const fileRef = ref(storage, fileName);
      await uploadBytes(fileRef, blob);
      const publicUrl = await getDownloadURL(fileRef);

      const q = firebaseQuery(
        collection(db, "progress"),
        where("title", "==", title),
        where("uid", "==", UID)
      );
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach(async (document) => {
        await updateDoc(doc(db, "progress", document.id), {
          image: publicUrl,
        });
      });
      setImageGeneratingForNodes((prev) =>
        prev.filter((item) => item !== title)
      );
    } catch (error) {
      console.error("Error saving custom image:", error);
      let userErrorMessage = `Error saving custom image: ${error.message}`;
      if (error.name === "TypeError" && error.message === "Failed to fetch") {
        userErrorMessage =
          "The image URL is restricted by CORS. Please try another image, or download it first and then upload the file.";
      }
      setSnack({ message: userErrorMessage, severity: "error" });
      setImageGeneratingForNodes((prev) =>
        prev.filter((item) => item !== title)
      );
      // No need to throw error further if we've handled it with a snackbar for the user.
      // If other parts of the application *need* to react to this specific error, re-throwing might be considered.
      // For now, informing the user and stopping the current operation is sufficient.
      return; // Return to prevent further processing or unhandled promise rejection if this function is part of a chain.
    }
  };

  // Async function to fetch a node by its title directly from Firestore
  async function findNodeByTitle(title) {
    const q = firebaseQuery(
      collection(db, "progress"),
      where("title", "==", title),
      where("uid", "==", UID)
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return null;
    }
    const docSnap = querySnapshot.docs[0];
    return { id: docSnap.id, ...docSnap.data() };
  }

  // Recursive function to retrieve descendants of a node based on the "parent" field
  async function getDescendants(node) {
    const q = firebaseQuery(
      collection(db, "progress"),
      where("parent", "==", node.title),
      where("uid", "==", UID)
    );
    const querySnapshot = await getDocs(q);
    const children = [];
    for (const docSnap of querySnapshot.docs) {
      const childNode = { id: docSnap.id, ...docSnap.data() };
      childNode.children = await getDescendants(childNode);
      children.push(childNode);
    }
    return children;
  }

  // Function to print the JSON representation of the "Exercise" node and all its descendants
  async function printExerciseJSON() {
    // Get the "Life" node (exact document data from Firestore)
    const lifeNode = await findNodeByTitle("Life");
    if (!lifeNode) {
      console.log('Node with title "Life" not found.');
      return;
    }

    // Get the direct child of "Life" with the title "Exercise"
    let exerciseNode = null;
    {
      const q = firebaseQuery(
        collection(db, "progress"),
        where("title", "==", "Exercise"),
        where("parent", "==", "Life"),
        where("uid", "==", UID)
      );
      const qs = await getDocs(q);
      if (!qs.empty) {
        exerciseNode = { id: qs.docs[0].id, ...qs.docs[0].data() };
      }
    }
    if (!exerciseNode) {
      console.log('Child node with title "Exercise" under "Life" not found.');
      return;
    }

    // Helper function to get one child (without adding a "children" field)
    async function getOneChild(parent) {
      const q = firebaseQuery(
        collection(db, "progress"),
        where("parent", "==", parent.title),
        where("uid", "==", UID)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      const childDoc = snapshot.docs[0];
      return { id: childDoc.id, ...childDoc.data() };
    }

    // Get the chain:
    // 1. "Life" node
    // 2. Its child "Exercise"
    // 3. One child of "Exercise" (if exists)
    // 4. One child of that node (if exists)
    // 5. One child of the previous node (if exists)
    const childLevel1 = await getOneChild(exerciseNode);
    const childLevel2 = childLevel1 ? await getOneChild(childLevel1) : null;
    const childLevel3 = childLevel2 ? await getOneChild(childLevel2) : null;

    // Create an array of the exact documents (without any extra children field)
    const chain = [lifeNode, exerciseNode];
    if (childLevel1) chain.push(childLevel1);
    if (childLevel2) chain.push(childLevel2);
    if (childLevel3) chain.push(childLevel3);

    console.log(JSON.stringify(chain, null, 2));
  }

  const handleDailyPointsSubmit = async (node) => {
    const docRef = doc(db, "progress", node.id);
    await updateDoc(docRef, { dailyPoints: editingNode.dailyPoints });
    setEditingDailyPoints(false);
    setEditingNode(null);
    setNewNodeId(null); // Update the new node ID
  };

  const handleUnitSubmit = async (node) => {
    const docRef = doc(db, "progress", node.id);
    await updateDoc(docRef, { unit: editingNode.unit });
    setEditingUnit(false);
    setEditingNode(null);
    setNewNodeId(null); // Update the new node ID
  };

  const findNodeAndParentById = (current, id, parent = null) => {
    if (!current) return null;
    if (current.id === id) return { node: current, parent };
    if (current.children) {
      for (const child of current.children) {
        const res = findNodeAndParentById(child, id, current);
        if (res) return res;
      }
    }
    return null;
  };

  const moveNodeUp = async (event, node) => {
    event.stopPropagation();
    if (!progressTree) return;

    const result = findNodeAndParentById(progressTree, node.id);
    if (!result || !result.parent) {
      console.error("Node or parent not found, or node is root.");
      return;
    }

    const parentNode = result.parent;
    let siblings = parentNode.children;

    siblings = await ensureSiblingOrder(parentNode, siblings);

    const idx = siblings.findIndex((s) => s.id === node.id);

    if (idx <= 0) {
      console.warn(
        "Node is already at the top or not found in siblings list after ensuring order."
      );
      return;
    }

    const upperSibling = siblings[idx - 1];

    try {
      await runTransaction(db, async (transaction) => {
        const nodeRef = doc(db, "progress", node.id);
        const upperSiblingRef = doc(db, "progress", upperSibling.id);

        const nodeDoc = await transaction.get(nodeRef);
        const upperSiblingDoc = await transaction.get(upperSiblingRef);

        if (!nodeDoc.exists() || !upperSiblingDoc.exists()) {
          throw new Error("One or both documents not found in transaction!");
        }

        const nodeOrder = nodeDoc.data().order;
        const upperSiblingOrder = upperSiblingDoc.data().order;

        if (nodeOrder === undefined || upperSiblingOrder === undefined) {
          // This case should ideally be prevented by ensureSiblingOrder
          console.error(
            "Order is undefined for one of the nodes in transaction. Aborting."
          );
          throw new Error("Order undefined in transaction.");
        }

        transaction.update(nodeRef, { order: upperSiblingOrder });
        transaction.update(upperSiblingRef, { order: nodeOrder });
      });
    } catch (error) {
      console.error("Transaction failed in moveNodeUp: ", error);
    }
  };

  const ensureSiblingOrder = async (parentNode, siblings) => {
    const updatedSiblings = [...siblings]; // Create a mutable copy

    // First pass: Ensure every sibling has an order, defaulting to its index
    // This helps if an order is not just missing from Firestore but also locally.
    for (let i = 0; i < updatedSiblings.length; i++) {
      if (updatedSiblings[i].order === undefined) {
        updatedSiblings[i].order = i;
        // No need to track orderChanged, we will always write if siblings exist.
      }
    }

    // Second pass: Sort by order and then definitively set sequence
    updatedSiblings.sort((a, b) => {
      // Handle potential non-numeric order values gracefully during sort
      // Items with non-numeric order (or null) will be treated as having a very large order,
      // effectively pushing them to the end before the final sequential assignment.
      const orderA =
        typeof a.order === "number" && !isNaN(a.order)
          ? a.order
          : Number.MAX_SAFE_INTEGER;
      const orderB =
        typeof b.order === "number" && !isNaN(b.order)
          ? b.order
          : Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    for (let i = 0; i < updatedSiblings.length; i++) {
      updatedSiblings[i].order = i; // Assign the correct sequential order
    }

    // Always commit the determined order for all siblings to Firestore
    // This makes this function the authority for sibling order before a move.
    if (updatedSiblings.length > 0) {
      const batch = writeBatch(db);
      updatedSiblings.forEach((sibling) => {
        if (sibling.id) {
          // Ensure sibling has an id
          const siblingRef = doc(db, "progress", sibling.id);
          batch.update(siblingRef, { order: sibling.order }); // sibling.order is now the definitive correct sequential order
        }
      });
      await batch.commit();
    }
    return updatedSiblings;
  };

  const moveNodeDown = async (event, node) => {
    event.stopPropagation();
    if (!progressTree) return;

    const result = findNodeAndParentById(progressTree, node.id);
    if (!result || !result.parent) {
      console.error("Node or parent not found.");
      return;
    }

    const parentNode = result.parent;
    let siblings = parentNode.children;

    siblings = await ensureSiblingOrder(parentNode, siblings);

    const idx = siblings.findIndex((s) => s.id === node.id);

    if (idx === -1 || idx >= siblings.length - 1) {
      console.warn(
        "Node is already at the bottom or not found in siblings list after ensuring order."
      );
      return;
    }

    const lowerSibling = siblings[idx + 1];

    try {
      await runTransaction(db, async (transaction) => {
        const nodeRef = doc(db, "progress", node.id);
        const lowerSiblingRef = doc(db, "progress", lowerSibling.id);

        const nodeDoc = await transaction.get(nodeRef);
        const lowerSiblingDoc = await transaction.get(lowerSiblingRef);

        if (!nodeDoc.exists() || !lowerSiblingDoc.exists()) {
          throw new Error("One or both documents not found in transaction!");
        }

        const nodeOrder = nodeDoc.data().order;
        const lowerSiblingOrder = lowerSiblingDoc.data().order;

        if (nodeOrder === undefined || lowerSiblingOrder === undefined) {
          // This case should ideally be prevented by ensureSiblingOrder
          console.error(
            "Order is undefined for one of the nodes in transaction. Aborting."
          );
          throw new Error("Order undefined in transaction.");
        }

        transaction.update(nodeRef, { order: lowerSiblingOrder });
        transaction.update(lowerSiblingRef, { order: nodeOrder });
      });
    } catch (error) {
      console.error("Transaction failed in moveNodeDown: ", error);
    }
  };

  const addNewChild = async (event, parent) => {
    event.stopPropagation(); // Prevent event from bubbling up

    if (!parent) return;

    let newTitle = "New Title";
    let q = firebaseQuery(
      collection(db, "progress"),
      where("title", "==", newTitle),
      where("uid", "==", UID)
    );
    let querySnapshot = await getDocs(q);

    while (!querySnapshot.empty) {
      newTitle += "1";
      q = firebaseQuery(
        collection(db, "progress"),
        where("title", "==", newTitle),
        where("uid", "==", UID)
      );
      querySnapshot = await getDocs(q);
    }
    const order = parent.children ? parent.children.length : 0;
    const newChildRef = await addDoc(collection(db, "progress"), {
      uid: UID,
      title: newTitle,
      points: 0,
      dailyPoints: 10,
      unit: "Points",
      parent: parent.title,
      order,
    });

    setNewNodeId(newChildRef.id); // Update the new node ID
    setEditingNode({ id: newChildRef.id, title: "New Title" }); // Set the new node for editing
    setEditingTitle(true);
    setExpandedItems((prevExpandedItems) => [...prevExpandedItems, parent.id]);

    // printExerciseJSON();
  };

  const handleFocus = (event) => {
    // Selects the whole text in the input field
    event.target.select();
  };

  const handleKeyPress = (e, node, fieldName) => {
    if (e.key === "Enter") {
      if (fieldName === "title") {
        handleTitleSubmit(node);
      } else if (fieldName === "dailyPoints") {
        handleDailyPointsSubmit(node);
      } else if (fieldName === "unit") {
        handleUnitSubmit(node);
      }
    }
  };

  const handleExpandedItemsChange = (event, itemIds) => {
    setExpandedItems(itemIds);
  };

  const handleExpandClick = () => {
    setExpandedItems((oldExpanded) =>
      oldExpanded.length === 0 ? getAllItemsWithChildrenItemIds() : []
    );
  };

  const getAllItemsWithChildrenItemIds = () => {
    const itemIds = [];
    const registerItemId = (item) => {
      itemIds.push(item.id);
      if (item.children?.length) {
        item.children.forEach(registerItemId);
      }
    };

    if (progressData && progressData.children) {
      progressData.children.forEach(registerItemId);
    }

    return itemIds;
  };

  const handleExpandedItemChange = (event, nodeId) => {
    setExpandedItems((prevExpandedItems) =>
      prevExpandedItems.includes(nodeId)
        ? prevExpandedItems.filter((id) => id !== nodeId)
        : [...prevExpandedItems, nodeId]
    );
  };

  function findNodePath(node, targetTitle, parentTitle, path = []) {
    if (!node) return null;
    const titleMatch = node.title?.toLowerCase() === targetTitle?.toLowerCase();
    const parentMatch =
      !parentTitle || node.parent?.toLowerCase() === parentTitle.toLowerCase();
    if (titleMatch && parentMatch) {
      return [...path, node.id];
    }
    if (node.children) {
      for (const child of node.children) {
        const res = findNodePath(child, targetTitle, parentTitle, [
          ...path,
          node.id,
        ]);
        if (res) return res;
      }
    }
    return null;
  }

  const findNodePathsByTitle = (node, targetTitle, path = [], results = []) => {
    if (!node) return results;
    if (node.title?.toLowerCase() === targetTitle?.toLowerCase()) {
      results.push([...path, node.id]);
    }
    if (node.children) {
      for (const child of node.children) {
        findNodePathsByTitle(child, targetTitle, [...path, node.id], results);
      }
    }
    return results;
  };

  const findPathById = (node, targetId, path = []) => {
    if (!node) return null;
    if (node.id === targetId) {
      return [...path, node.id];
    }
    if (node.children) {
      for (const child of node.children) {
        const res = findPathById(child, targetId, [...path, node.id]);
        if (res) return res;
      }
    }
    return null;
  };

  const applyGainOrLosePoints = (suggestions) => {
    if (!progressData || !suggestions) return;
    const expand = new Set(expandedItems);
    const highlight = {};
    suggestions.forEach(({ title, parent, change }) => {
      const path = findNodePath(progressData, title, parent);
      const paths = findNodePathsByTitle(progressData, title);
      if (path && path.length) {
        path.forEach((id) => expand.add(id));
        const nodeId = path[path.length - 1];
        highlight[nodeId] = change === "lose" ? "lose" : "gain";
      }
      paths.forEach((p) => {
        if (p && p.length) {
          p.forEach((id) => expand.add(id));
          const nodeId = p[p.length - 1];
          highlight[nodeId] = change === "lose" ? "lose" : "gain";
        }
      });
    });
    setExpandedItems(Array.from(expand));
    setHighlightedNodes(highlight);
  };

  const showImprovement = (imp) => {
    if (!imp || !progressData) return;
    const expand = new Set(expandedItems);
    const highlight = {};
    let path = null;
    if (imp.type === "add") {
      path = findNodePath(progressData, imp.parent, null);
    } else if (imp.type === "modify") {
      path = findNodePath(progressData, imp.old_title, imp.old_parent);
    } else if (imp.type === "delete") {
      path = findNodePath(progressData, imp.title, imp.parent);
    }
    if (path && path.length) {
      path.forEach((id) => expand.add(id));
      const nodeId = path[path.length - 1];
      highlight[nodeId] = "focus";
    }
    setExpandedItems(Array.from(expand));
    setHighlightedNodes(highlight);
  };

  const handleGainOrLosePointsStepChange = useCallback(
    (pointItem) => {
      if (!progressData || !pointItem || !pointItem.title) {
        console.warn(
          "Dashboard: handleGainOrLosePointsStepChange missing data",
          { progressData, pointItem }
        );
        return;
      }

      // findNodePath is defined in Dashboard's scope and doesn't change based on props/state directly.
      // It operates on progressData which is a dependency.
      const path = findNodePath(progressData, pointItem.title, null);

      if (path && path.length > 0) {
        const nodeId = path[path.length - 1];
        const highlightType = pointItem.change === "gain" ? "gain" : "lose";

        setHighlightedNodes({ [nodeId]: highlightType });

        setExpandedItems((prevExpandedItems) => {
          const newExpanded = new Set(prevExpandedItems);
          path.forEach((id) => newExpanded.add(id));
          return Array.from(newExpanded);
        });
      } else {
        console.warn(
          `Dashboard: Node with title "${pointItem.title}" not found for highlighting.`
        );
      }
    },
    [progressData, setHighlightedNodes, setExpandedItems] // findNodePath is stable if defined outside or only uses its args + progressData
  );

  const applyImprovement = async (imp) => {
    if (!imp) return;
    try {
      if (imp.type === "add") {
        const newDocData = {
          uid: UID,
          title: imp.title,
          parent: imp.parent, // This is the title of the parent
          points: 0,
          dailyPoints: imp.dailyPoints || 0,
          unit: imp.unit || "Points",
          order: imp.order !== undefined ? imp.order : 0, // Default order if not specified
          // Include other potential fields from the improvement proposal
          ...(imp.description && { description: imp.description }),
          ...(imp.isUrgent && { isUrgent: imp.isUrgent }),
          // ensure embedding is not copied if present on imp object
        };
        // Remove undefined fields from newDocData to avoid Firebase errors
        Object.keys(newDocData).forEach((key) => {
          if (newDocData[key] === undefined) {
            delete newDocData[key];
          }
        });

        const newDocRef = await addDoc(collection(db, "progress"), newDocData);
        const newNodeId = newDocRef.id;

        // After adding the document, update local state to ensure visibility
        if (progressData && imp.parent) {
          // Find the path to the parent node. imp.parent is its title.
          // The findNodePath function is defined in Dashboard.js:
          // findNodePath(nodeToSearchIn, titleOfNodeToFind, titleOfParentOfNodeToFind, currentPathArray)
          // We are looking for the node whose title is imp.parent. We search within progressData.
          // We do not need to specify the parent of imp.parent, so the third argument is null.
          const parentPathArray = findNodePath(progressData, imp.parent, null);

          if (parentPathArray && parentPathArray.length > 0) {
            const parentNodeIdToExpand =
              parentPathArray[parentPathArray.length - 1];

            // Only add to expandedItems if it is not the root node itself.
            // The root node (progressData.id) is not typically managed by 'expandedItems'.
            if (parentNodeIdToExpand !== progressData.id) {
              setExpandedItems((prevExpandedItems) => {
                const newSet = new Set(prevExpandedItems);
                newSet.add(parentNodeIdToExpand);
                return Array.from(newSet);
              });
            }
          } else {
            console.log(
              "Dashboard.js: Parent node (title:",
              imp.parent,
              ") for new item not found in current progressData. Cannot expand."
            );
          }
        } else if (!imp.parent) {
          // This case means the new node is a direct child of "Life" (the root).
          // No specific parent needs to be expanded via expandedItems, as root children are top-level.
          console.log(
            "Dashboard.js: New node is a child of root or has no specified parent."
          );
        }

        // Highlight the new node to trigger scrolling.
        // The existing useEffect hook with a 1s delay should pick this up.
        setHighlightedNodes({ [newNodeId]: "focus" }); // "focus" is an existing highlight style
      } else if (imp.type === "modify") {
        if (
          typeof imp.old_title !== "string" ||
          imp.old_title.trim() === "" ||
          typeof imp.old_parent !== "string" ||
          imp.old_parent.trim() === ""
        ) {
          console.error(
            "Error: Missing or invalid 'old_title' or 'old_parent' for modify operation. Improvement data: ",
            imp
          );
          return;
        }

        const q = firebaseQuery(
          collection(db, "progress"),
          where("title", "==", imp.old_title),
          where("parent", "==", imp.old_parent),
          where("uid", "==", UID)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const id = snap.docs[0].id;
          const updateData = {};

          if (imp.new_title) {
            updateData.title = imp.new_title;
          } else if (imp.old_title) {
            updateData.title = imp.old_title;
          }

          if (imp.new_parent) {
            updateData.parent = imp.new_parent;
          } else if (imp.old_parent) {
            updateData.parent = imp.old_parent;
          }

          if (imp.dailyPoints !== undefined) {
            updateData.dailyPoints = imp.dailyPoints;
          }
          if (imp.unit !== undefined) {
            updateData.unit = imp.unit;
          }
          // Add other updatable fields here as needed, for example:
          // if (imp.description !== undefined) updateData.description = imp.description;
          // if (imp.isUrgent !== undefined) updateData.isUrgent = imp.isUrgent;
          // if (imp.order !== undefined) updateData.order = imp.order;

          if (Object.keys(updateData).length > 0) {
            await updateDoc(doc(db, "progress", id), updateData);
          } else {
            console.log(
              "No valid fields to update for node ID:",
              id,
              "Improvement data:",
              imp
            );
          }
        } else {
          console.log("Node not found for modification:", imp);
        }
      } else if (imp.type === "delete") {
        const q = firebaseQuery(
          collection(db, "progress"),
          where("title", "==", imp.title),
          where("parent", "==", imp.parent),
          where("uid", "==", UID)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const node = { id: snap.docs[0].id, ...snap.docs[0].data() };
          const descendants = await getDescendants(node);
          const promises = descendants.map((d) =>
            deleteDoc(doc(db, "progress", d.id))
          );
          promises.push(deleteDoc(doc(db, "progress", node.id)));
          await Promise.all(promises);
        }
      }
    } catch (err) {
      console.error("Failed to apply improvement", err);
    }
  };

  const handleGainOrLosePoints = (suggestions) => {
    if (!suggestions) return;
    if (!progressData) {
      setPendingSuggestions(suggestions);
      return;
    }
    applyGainOrLosePoints(suggestions);
  };

  useEffect(() => {
    if (pendingSuggestions && progressData) {
      applyGainOrLosePoints(pendingSuggestions);
      setPendingSuggestions(null);
    }
  }, [pendingSuggestions, progressData]);

  useEffect(() => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    const ids = Object.keys(highlightedNodes || {});
    if (ids.length > 0) {
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedNodes({});
        highlightTimeoutRef.current = null;
      }, 10000);
    }
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, [highlightedNodes]);

  useEffect(() => {
    const ids = Object.keys(highlightedNodes || {});
    if (!treeViewRef.current || ids.length === 0) return;

    const handle = setTimeout(() => {
      console.log("Scrolling to node:", ids[0]);
      const el = document.getElementById(`tree-item-${ids[0]}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 1000);
    return () => clearTimeout(handle);
  }, [highlightedNodes, expandedItems]);

  useEffect(() => {
    if (searchResults.length !== 0 && searchResults[currentSearchResultIndex]) {
      setExpandedItems((prevExpandedItems) => {
        const currentResult = searchResults[currentSearchResultIndex];
        const nodeId = currentResult.id;

        setHighlightedNodes({ [nodeId]: "focus" });

        const path = findPathById(progressData, nodeId);
        const newExpandedItems = new Set(prevExpandedItems);
        if (path && path.length) {
          path.forEach((id) => newExpandedItems.add(id));
        }
        return Array.from(newExpandedItems);
      });
    }
  }, [currentSearchResultIndex, searchResults, progressData]);

  const performSearch = async (query) => {
    if (!query || !progressData) {
      setHighlightedNodes({});
      return;
    }
    if (!UID) {
      console.error("UID not available for search.");
      return;
    }
    setIsSearching(true);
    try {
      // 1. Fetch query embedding
      const queryEmbedding = await embedText(query);
      if (!queryEmbedding) {
        console.error("Failed to generate query embedding.");
        setHighlightedNodes({});
        return;
      }

      // 2. Fetch all embeddings for the current user from 'progressEmbeddings'
      const embeddingsMap = new Map();
      const embeddingsQuery = firebaseQuery(
        // Renamed to avoid conflict with local 'query' variable
        collection(db, "progressEmbeddings"),
        where("uid", "==", UID)
      );
      const embeddingsSnapshot = await getDocs(embeddingsQuery);
      embeddingsSnapshot.forEach((doc) => {
        embeddingsMap.set(doc.id, doc.data().embedding);
      });

      if (embeddingsMap.size === 0) {
        console.log("No embeddings found in progressEmbeddings for this user.");
        setHighlightedNodes({});
        return;
      }

      // 3. Traverse progressData and calculate similarity
      const results = [];
      const traverse = (node) => {
        if (!node) return;
        // Use node.id to look up its embedding
        const nodeEmbedding = embeddingsMap.get(node.id);
        if (nodeEmbedding && Array.isArray(nodeEmbedding)) {
          const score = cosineSimilarity(nodeEmbedding, queryEmbedding);
          results.push({ id: node.id, score, title: node.title }); // Added title
        }
        if (node.children) node.children.forEach(traverse);
      };

      traverse(progressData);
      results.sort((a, b) => b.score - a.score);

      setSearchResults(results.slice(0, 10)); // Limit to top 10 results

      if (results.length > 0) {
        setCurrentSearchResultIndex(0);
        const firstResult = results[0];
        const highlight = { [firstResult.id]: "focus" };
        const expand = new Set(expandedItems);
        const path = findPathById(progressData, firstResult.id);
        if (path) {
          path.forEach((id) => expand.add(id));
        }
        setExpandedItems(Array.from(expand));
        setHighlightedNodes(highlight);
      } else {
        setCurrentSearchResultIndex(0);
        setHighlightedNodes({});
      }
    } catch (err) {
      console.error("Search error", err);
    } finally {
      setIsSearching(false);
    }
  };

  const getDescendantCount = (node) => {
    if (!node.children) return 0;
    return node.children.reduce(
      (count, child) => count + 1 + getDescendantCount(child),
      0
    );
  };

  const cosineSimilarity = (a, b) => {
    const dot = a.reduce((sum, val, i) => sum + val * b[i], 0);
    const magA = Math.sqrt(a.reduce((sum, val) => sum + val * val, 0));
    const magB = Math.sqrt(b.reduce((sum, val) => sum + val * val, 0));
    return dot / (magA * magB);
  };

  const renderTree = (node, siblingsOfNode) => {
    if (!node || typeof node.title === "undefined" || node.title === null) {
      return null; // Skip rendering if node is null or title is not defined
    }

    let siblingMaxAccumulatedPoints = 0;
    if (siblingsOfNode && siblingsOfNode.length > 0) {
      siblingMaxAccumulatedPoints = Math.max(
        ...siblingsOfNode.map((s) => s.totalAccumulatedPoints || 0)
      );
    }
    if (siblingMaxAccumulatedPoints === 0 && node.totalAccumulatedPoints > 0) {
      siblingMaxAccumulatedPoints = node.totalAccumulatedPoints;
    }

    const descendantCount = getDescendantCount(node);
    const dayPercentage = Math.round(
      Math.min(
        (node.today ? node.today : 0) /
          (!descendantCount || descendantCount === 0 ? 10 : descendantCount),
        1
      ) * 100
    );

    return (
      <TreeItem
        key={node.id}
        nodeId={node.id}
        id={`tree-item-${node.id}`}
        onClick={(event) => handleExpandedItemChange(event, node.id)}
        label={
          <TreeNodeLabel
            node={node}
            dayPercentage={dayPercentage}
            totalAccumulatedPoints={node.totalAccumulatedPoints || 0}
            siblingMaxAccumulatedPoints={siblingMaxAccumulatedPoints}
            showPoints={showPoints}
            editingNode={editingNode}
            editingTitle={editingTitle}
            editingDailyPoints={editingDailyPoints}
            editingUnit={editingUnit}
            newNodeId={newNodeId}
            inputRef={inputRef}
            showImageButtons={showImageButtons}
            showImages={showImages}
            showNodeButtons={showNodeButtons}
            imageGeneratingForNodes={imageGeneratingForNodes}
            highlightedNodes={highlightedNodes}
            setEditingNode={setEditingNode}
            setEditingTitle={setEditingTitle}
            setEditingDailyPoints={setEditingDailyPoints}
            setEditingUnit={setEditingUnit}
            handleTitleChange={handleTitleChange}
            handleTitleSubmit={handleTitleSubmit}
            handleKeyPress={handleKeyPress}
            handleFocus={handleFocus}
            regenerateImageForTitle={regenerateImageForTitle}
            saveCustomImageToGCS={saveCustomImageToGCS}
            updateNodePoints={updateNodePoints}
            handleDecrementOpen={handleDecrementOpen}
            handleDailyPointsChange={handleDailyPointsChange}
            handleDailyPointsSubmit={handleDailyPointsSubmit}
            handleUnitChange={handleUnitChange}
            handleUnitSubmit={handleUnitSubmit}
            addNewChild={addNewChild}
            moveNodeUp={moveNodeUp}
            moveNodeDown={moveNodeDown}
            handleDeleteOpen={handleDeleteOpen}
            isUrgent={node.isUrgent || false}
            onToggleUrgent={() => toggleNodeUrgentStatus(node)}
          />
        }
      >
        {node.children &&
          node.children.map((childNode) =>
            renderTree(childNode, node.children)
          )}
      </TreeItem>
    );
  };

  const setTheTimer = async () => {
    const totalSeconds =
      newTimerHours * 3600 + newTimerMinutes * 60 + newTimerSeconds;
    setDurationToAlert(totalSeconds);
    setTimer(totalSeconds);
    updateUserData(UID, {
      durationToAlert: totalSeconds,
    });
    setShowAlert(false);
    setTimerDialogOpen(false);
    await updateLastResetTime(UID);
  };

  const openTimerDialog = () => {
    setTimerDialogOpen(true);
    setNewTimerHours(Math.floor(timer / 3600));
    setNewTimerMinutes(Math.floor((timer % 3600) / 60));
    setNewTimerSeconds(timer % 60);
  };

  /**
   * Recursively finds and returns the node matching the given title in the progress tree.
   * @param {object} node - current node or subtree
   * @param {string} title - title to search for
   * @returns {object|null}
   */
  const filterProgress = (node, title) => {
    if (!node) return null;
    if (node.title === title) return node;
    if (node.children && node.children.length) {
      for (const child of node.children) {
        const found = filterProgress(child, title);
        if (found) return found;
      }
    }
    return null;
  };

  const chatOffset = chatExpanded ? chatboxHeight : "0px";
  const containerHeight = horizontal
    ? `calc(100vh - ${navbarHeight} - ${chatOffset})`
    : chatExpanded
    ? `calc(100vh - ${navbarHeight} - ${chatboxHeight})`
    : `calc(100vh - ${navbarHeight})`;
  const treeHeight = horizontal
    ? `calc(100vh - ${navbarHeight} - ${chatOffset})`
    : chatExpanded
    ? `calc(50vh - ${navbarHeight})`
    : `calc(100vh - ${navbarHeight})`;

  return (
    <div
      style={{
        display: "flex",
        height: containerHeight,
        width: "100%",
        overflow: "hidden",
        flexDirection: horizontal ? "row" : "column",
      }}
    >
      {horizontal && chatExpanded ? (
        <div
          style={{
            width: "50%",
            height: `calc(100vh - ${navbarHeight} - ${chatboxHeight})`,
            borderRight: "1px solid #333",
            borderTop: "0px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ChatBox
            uid={UID}
            agents={agents}
            onGainOrLosePoints={handleGainOrLosePoints}
            onShowImprovement={showImprovement}
            onApplyImprovement={applyImprovement}
            onGainOrLosePointsStepChange={handleGainOrLosePointsStepChange}
          />
        </div>
      ) : null}
      <div
        style={{
          width: horizontal ? (chatExpanded ? "50%" : "100%") : "100%",
          height: treeHeight,
          overflow: "auto",
          backgroundColor: showTimer
            ? `rgba(255,0,0,${(durationToAlert - timer) / durationToAlert})`
            : undefined,
        }}
      >
        <div>
          <DeleteDialog
            open={deleteConfirmDialogOpen}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            hasChildren={deleteHasChildren}
          />
          <DecrementDialog
            open={confirmDialogOpen}
            onClose={handleDecrementCancel}
            onConfirm={handleDecrementConfirm}
          />

          {showAlert && (
            <Alert
              severity="error"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                fontSize: "34px",
                zIndex: 1000,
              }}
            >
              10 Minutes Passed...
              <Button onClick={resetTimer}>Reset Timer</Button>
            </Alert>
          )}
          <div
            style={{
              display: "flex",
              padding: "10px",
              position: "sticky",
              top: 0,
              backgroundColor: theme.palette.background.paper,
              zIndex: 1,
            }}
          >
            {horizontal ? (
              <Button
                onClick={handleExpandClick}
                size="medium"
                sx={{ m: 0, mt: 1, height: 40, pb: 0, pt: 0 }}
                data-testid="dashboard-expand-collapse-all-button"
              >
                {expandedItems.length === 0 ? (
                  <ExpandMoreIcon />
                ) : (
                  <ExpandLessIcon />
                )}
                {expandedItems.length === 0 ? "Expand all" : "Collapse all"}
              </Button>
            ) : (
              <IconButton
                size="small"
                onClick={handleExpandClick}
                sx={{ m: 0 }}
                aria-label={
                  expandedItems.length === 0 ? "Expand all" : "Collapse all"
                }
                data-testid="dashboard-expand-collapse-all-button-mobile"
              >
                {expandedItems.length === 0 ? (
                  <ExpandMoreIcon />
                ) : (
                  <ExpandLessIcon />
                )}
              </IconButton>
            )}
            <TextField
              size="small"
              placeholder="Search"
              value={searchQuery}
              disabled={isSearching}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="dashboard-search-input" // Added data-testid
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  performSearch(searchQuery);
                }
              }}
              sx={{ mx: 2, flexGrow: 1 }}
              InputProps={{
                startAdornment: searchQuery && (
                  <InputAdornment position="start" sx={{ mr: 1 }}>
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      sx={{ p: 0 }}
                      data-testid="dashboard-clear-search-button"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ ml: 0, mr: "-4px" }}>
                    {isSearching ? (
                      <CircularProgress size={20} />
                    ) : (
                      <IconButton
                        size="small"
                        disabled={isSearching}
                        onClick={() => performSearch(searchQuery)}
                        sx={{ p: 0 }}
                        data-testid="dashboard-search-button" // Added data-testid
                      >
                        <SearchIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            {showTimer && (
              <>
                <IconButton
                  size="small"
                  onClick={openTimerDialog}
                  sx={{ color: "white", marginRight: "10px" }}
                >
                  <EditIcon />
                </IconButton>
                <Dialog
                  data-testid="dashboard-timer-edit-dialog" // Added data-testid for dialog itself
                  open={timerDialogOpen}
                  onClose={() => setTimerDialogOpen(false)}
                >
                  <DialogTitle>Set Timer</DialogTitle>
                  <DialogContent>
                    <TextField
                      autoFocus
                      margin="dense"
                      label="Hours"
                      type="number"
                      fullWidth
                      value={newTimerHours}
                      onChange={(e) => setNewTimerHours(Number(e.target.value))}
                    />
                    <TextField
                      margin="dense"
                      label="Minutes"
                      type="number"
                      fullWidth
                      value={newTimerMinutes}
                      onChange={(e) =>
                        setNewTimerMinutes(Number(e.target.value))
                      }
                    />
                    <TextField
                      margin="dense"
                      label="Seconds"
                      type="number"
                      fullWidth
                      value={newTimerSeconds}
                      onChange={(e) =>
                        setNewTimerSeconds(Number(e.target.value))
                      }
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setTimerDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={setTheTimer}>Save</Button>
                  </DialogActions>
                </Dialog>
                <span style={{ fontSize: "25px" }}>
                  {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, "0")}
                </span>
                <IconButton
                  onClick={stopPlay}
                  style={{
                    color: stopd ? "red" : "white",
                    marginLeft: "10px",
                    marginTop: "-5.5px",
                  }}
                  data-testid="dashboard-timer-stop-play-button" // Added data-testid
                >
                  {stopd ? (
                    <PlayCircleOutlineIcon fontSize="large" />
                  ) : (
                    <StopCircleIcon fontSize="large" />
                  )}
                </IconButton>
              </>
            )}
            <span
              data-testid="dashboard-timer-display"
              style={{ display: showTimer ? "inline" : "none" }}
            >
              {" "}
              {/* Conditional display for timer text itself */}
              {/* Timer text is already rendered above, this span is for targeting the display area if needed */}
            </span>
            <span></span>
            <Button
              size="medium"
              onClick={(e) => addNewChild(e, progressData)}
              sx={{ color: "orange", m: 0, mt: 1, height: 40, pb: 0, pt: 0 }}
              aria-label="Add Goal"
              data-testid="dashboard-add-root-goal-button" // Added data-testid
            >
              {horizontal && "Add Goal "}
              <AddIcon />
            </Button>
          </div>
          {searchResults.length > 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                maxHeight: "25px",
                marginBottom: "10px",
                marginLeft: "10px",
                position: "sticky",
                top: "50px",
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
              }}
            >
              {searchResults[currentSearchResultIndex] && (
                <Typography
                  variant="subtitle1"
                  sx={{ paddingRight: 1, paddingLeft: 1, maxHeight: "25px" }}
                >
                  Result: {searchResults[currentSearchResultIndex].title}
                </Typography>
              )}
              <MobileStepper
                steps={searchResults.length}
                position="static"
                activeStep={currentSearchResultIndex}
                sx={{ flexGrow: 1, maxHeight: "25px" }}
                nextButton={
                  <Button
                    size="small"
                    onClick={() =>
                      setCurrentSearchResultIndex((prev) =>
                        Math.min(prev + 1, searchResults.length - 1)
                      )
                    }
                    disabled={
                      currentSearchResultIndex === searchResults.length - 1
                    }
                  >
                    Next
                    {theme.direction === "rtl" ? (
                      <KeyboardArrowLeft />
                    ) : (
                      <KeyboardArrowRight />
                    )}
                  </Button>
                }
                backButton={
                  <Button
                    size="small"
                    onClick={() =>
                      setCurrentSearchResultIndex((prev) =>
                        Math.max(prev - 1, 0)
                      )
                    }
                    disabled={currentSearchResultIndex === 0}
                  >
                    {theme.direction === "rtl" ? (
                      <KeyboardArrowRight />
                    ) : (
                      <KeyboardArrowLeft />
                    )}
                    Back
                  </Button>
                }
              />
            </div>
          )}
          <TreeView
            ref={treeViewRef}
            // defaultExpanded={
            //   progressData &&
            //   progressData.children &&
            //   progressData.children.map((child) => child.id)
            // }
            expanded={expandedItems}
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
            sx={{ overflow: "auto" }}
          >
            {progressData &&
              progressData.children &&
              progressData.children.map((child) =>
                renderTree(child, progressData.children)
              )}
          </TreeView>
          {/* <div
        style={{
          width: "5px",
          cursor: "ew-resize",
          backgroundColor: "#ccc",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onMouseDown={handleDragStart}
      >
        <div
          style={{ width: "1px", height: "50%", backgroundColor: "black" }}
        />
      </div> */}
        </div>
        <Snackbar
          open={!!snack.message}
          autoHideDuration={6000}
          onClose={() => setSnack({ message: "", severity: "info" })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnack({ message: "", severity: "info" })}
            severity={snack.severity}
            sx={{ width: "100%" }}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </div>
      {!horizontal && chatExpanded ? (
        <div
          style={{
            width: "100%",
            height: horizontal
              ? `calc(100vh - ${navbarHeight} - ${chatboxHeight})`
              : `calc(50vh - ${navbarHeight})`,
            borderRight: "0px",
            borderTop: "1px solid #333",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ChatBox
            uid={UID}
            agents={agents}
            onGainOrLosePoints={handleGainOrLosePoints}
            onShowImprovement={showImprovement}
            onApplyImprovement={applyImprovement}
            onGainOrLosePointsStepChange={handleGainOrLosePointsStepChange}
          />
        </div>
      ) : null}
    </div>
  );
};

export default Dashboard;
